import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  TemplateRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, race, take } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { IconModule } from 'ish-core/icon.module';

export interface ModalOptions extends NgbModalOptions {
  /**
   * Modal title string.
   */
  titleText?: string;
  /**
   * Optional modal confirm button text.
   */
  confirmText?: string;
  /**
   * Optional modal confirm button disabled.
   */
  confirmDisabled?: boolean;
  /**
   * Optional modal reject button text.
   */
  rejectText?: string;
  /**
   * Optional icon properties to display an icon in front of the title, e.g. ['fas', 'triangle-exclamation'],
   */
  icon?: IconProp;
  /**
   * Optional icon styling classes, e.g. faIconClass: 'text-warning pr-2'
   */
  iconClass?: string;
}

/**
 * The Modal Dialog Component displays a generic (ng-bootstrap) modal, that shows a custom title and custom content.
 * It provides an optional footer that includes confirm and reject buttons.
 * It is possible to pass any data on show.
 * The also provided confirmed output emitter will return the previously passed data if the modal gets confirmed.
 *
 * @see https://ng-bootstrap.github.io/#/components/modal/api#NgbModal
 *
 * @example
 * <ish-modal-dialog [options]="options" (confirmed)="onConfirmed($event)">
 *   Dummy content
 * </ish-modal-dialog>
 */
@Component({
  selector: 'ish-modal-dialog',
  templateUrl: './modal-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, IconModule],
})
export class ModalDialogComponent<T> implements OnDestroy {
  private ngbModal = inject(NgbModal);
  private document = inject<Document>(DOCUMENT);

  readonly options = input.required<ModalOptions>();

  readonly confirmed = output<T>();

  readonly closed = output<T>();

  readonly shown = output<T>();

  readonly modalDialogTemplate = viewChild<TemplateRef<unknown>>('template');

  // visible-for-testing
  ngbModalRef: NgbModalRef;

  data: T;

  uuid = uuid();

  private hide$ = new Subject<void>();

  private destroyRef = inject(DestroyRef);

  /**
   * Configure and show modal dialog.
   */
  show(data?: T) {
    this.data = data ? data : undefined;

    this.ngbModalRef = this.ngbModal.open(this.modalDialogTemplate(), {
      ...this.options(),
      ariaLabelledBy: `modal-title-${this.uuid}`,
    });

    race(this.ngbModalRef.dismissed, this.hide$)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closed.emit(this.data);
      });

    this.shown.emit(this.data);
  }

  /**
   * Hides modal dialog.
   */
  hide() {
    this.ngbModalRef.close();
    this.hide$.next();
  }

  /**
   * Emits input data or undefined and hides modal.
   */
  confirm() {
    this.confirmed.emit(this.data);
    this.hide();
  }

  /**
   * Scrolls to an anchor element
   *
   * @param anchor The ID of the anchor element.
   */
  // not-dead-code
  scrollToAnchor(anchor: string) {
    if (this.options().scrollable) {
      const element = this.document.getElementById(anchor);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }, 200);
      }
    }
  }

  ngOnDestroy(): void {
    this.hide$.complete(); // complete open hide$ subscription
  }
}
